import React from 'react';
import { Eye, Download, History } from 'lucide-react';
import { exportToCSV } from '../utils/adminHelpers';
import { useAdmin } from '../context/AdminContext';
import apiService from '../apiService';
import Img from '../components/Img';
import CityFilter, { useCities, cityLabel } from '../components/CityFilter';

// Libellés de catégorie (standard = Mbiyo)
const CAT_LABELS = { standard: 'Mbiyo', confort: 'Confort', premium: 'Premium', moto: 'Moto' };
// Rôles effectifs d'un chauffeur : approuvés si présents, sinon demandés (pour les 'pending').
const driverRoles = (d) => ((d.approved_roles && d.approved_roles.length ? d.approved_roles : d.requested_roles) || ['driver']);
const roleTypeLabel = (roles) => {
  const isDriver = roles.includes('driver'), isDeliverer = roles.includes('deliverer');
  if (isDriver && isDeliverer) return 'Chauffeur + Livreur';
  if (isDeliverer) return 'Livreur';
  return 'Chauffeur';
};

export default function DriversPage() {
  const {
    loadingDrivers, drivers, statusFilter, setStatusFilter,
    setSelectedDriver, setEditingDriver, setEditDriverFormData, setShowEditDriverModal,
    setResetPasswordUser, setResetPasswordResult, setShowResetPasswordModal,
  } = useAdmin();
  const [roleFilter, setRoleFilter] = React.useState('all'); // 'all' | 'driver' | 'deliverer'
  const [cityFilter, setCityFilter] = React.useState(''); // '' = toutes les villes
  const [historyDriver, setHistoryDriver] = React.useState(null); // chauffeur dont on affiche l'historique
  const cities = useCities();

  if (loadingDrivers) {
    return (
      <div className="dashboard-content">
        <h1 className="page-title">Gestion des Chauffeurs</h1>
        <div className="loading-spinner"><div className="spinner"></div><p>Chargement des chauffeurs...</p></div>
      </div>
    );
  }

  const filteredDrivers = (statusFilter === 'all'
    ? drivers.filter(d => ['approved', 'suspended'].includes(d.status))
    : drivers.filter(d => d.status === statusFilter)
  ).filter(d => roleFilter === 'all' || driverRoles(d).includes(roleFilter))
    .filter(d => !cityFilter || d.city_slug === cityFilter);

  return (
    <div className="dashboard-content">
      <div className="page-header">
        <h1 className="page-title">Gestion des Chauffeurs</h1>
        <div className="page-actions">
          <select className="filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">Tous les chauffeurs</option>
            <option value="approved">Approuvés seulement</option>
            <option value="rejected">Rejetés</option>
            <option value="suspended">Suspendus</option>
          </select>
          <select className="filter-select" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
            <option value="all">Tous les types</option>
            <option value="driver">Chauffeurs seulement</option>
            <option value="deliverer">Livreurs seulement</option>
          </select>
          <CityFilter value={cityFilter} onChange={setCityFilter} cities={cities} />
          <button className="btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
            onClick={() => exportToCSV('chauffeurs', filteredDrivers.map(d => ({
              Nom: d.name || d.driver_name || '—',
              Téléphone: d.phone || d.driver_phone || '—',
              Email: d.email || d.driver_email || '—',
              Statut: d.status || '—',
              Catégories: (d.driver_categories || d.categories || []).join(', '),
              Rôles: (d.approved_roles || []).join(', '),
              Ville: d.city_slug || '—',
              Inscription: d.created_at ? new Date(d.created_at).toLocaleDateString('fr-FR') : '—',
            })))}
          >
            <Download size={16} /> Exporter CSV
          </button>
        </div>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Chauffeur</th><th>Téléphone</th><th>Ville</th><th>Type</th><th>Catégorie</th><th>Véhicule</th><th>Plaque</th>
              <th>Statut</th><th>Note</th><th>Scans (Aujourd'hui)</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredDrivers.map(driver => (
              <tr key={driver.id}>
                <td>
                  <div className="driver-cell" onClick={() => setSelectedDriver(driver)} style={{ cursor: 'pointer' }}>
                    {driver.photo ? (
                      <Img src={driver.photo} alt={driver.name} className="table-avatar" />
                    ) : (
                      <div className="table-avatar no-photo">{driver.name?.charAt(0)}</div>
                    )}
                    <span style={{ textDecoration: 'underline', color: '#C9350F', fontWeight: '500' }}>{driver.name}</span>
                  </div>
                </td>
                <td>{driver.phone}</td>
                <td>{cityLabel(cities, driver.city_slug)}</td>
                <td>{roleTypeLabel(driverRoles(driver))}</td>
                <td>{(driver.categories || ['standard']).map(c => CAT_LABELS[c] || c).join(', ')}</td>
                <td>{driver.vehicle?.brand} {driver.vehicle?.model}</td>
                <td><span className="plate-badge">{driver.vehicle?.plate}</span></td>
                <td>
                  <span className={`status-badge status-${driver.status}`}>
                    {driver.status === 'pending' && 'En attente'}
                    {driver.status === 'approved' && 'Approuvé'}
                    {driver.status === 'rejected' && 'Rejeté'}
                    {driver.status === 'suspended' && '🚫 Suspendu'}
                  </span>
                </td>
                <td>
                  {(driver.rating_count || 0) === 0
                    ? <span style={{ color: '#9AA0A8', fontSize: '0.8rem' }} title="Aucun avis reçu">Nouveau</span>
                    : <div className="rating" title={`${driver.rating_count} avis${driver.cancellation_stats?.penalty ? ` · pénalité annulations −${driver.cancellation_stats.penalty}` : ''}`}>⭐ {Number(driver.rating).toFixed(2)} ({driver.rating_count}){driver.rating_watch?.active ? ' ⚠' : ''}</div>}
                </td>
                <td style={{ textAlign: 'center', fontWeight: '600', color: '#C9350F' }}>{driver.scanStats?.today || '-'}</td>
                <td>
                  <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
                    <button className="icon-btn" onClick={() => setSelectedDriver(driver)}><Eye size={18} /></button>
                    <button onClick={() => {
                      setEditingDriver(driver);
                      setEditDriverFormData({
                        driver_name: driver.name || '', driver_phone: driver.phone || '', driver_email: driver.email || '',
                        vehicle_brand: driver.vehicle?.brand || '', vehicle_model: driver.vehicle?.model || '',
                        vehicle_color: driver.vehicle?.color || '', vehicle_plate: driver.vehicle?.plate || '',
                        vehicle_year: driver.vehicle?.year || '', driver_categories: driver.categories || ['standard'],
                        license_number: driver.license_number || '', id_number: driver.id_number || '',
                        approved_roles: driver.approved_roles || ['driver']
                      });
                      setShowEditDriverModal(true);
                    }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1rem', padding: '0.5rem', color: '#C9350F' }} title="Modifier">✏️</button>
                    <button onClick={() => {
                      setResetPasswordUser({ ...driver, user_id: driver.driver_id, _isDriver: true });
                      setResetPasswordResult(null);
                      setShowResetPasswordModal(true);
                    }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1rem', padding: '0.5rem', color: '#dc2626' }} title="Réinitialiser mot de passe">🔑</button>
                    <button className="icon-btn" onClick={() => setHistoryDriver(driver)} title="Historique des courses" style={{ color: '#FF5500' }}><History size={18} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {historyDriver && (
        <DriverHistoryModal driver={historyDriver} onClose={() => setHistoryDriver(null)} />
      )}
    </div>
  );
}

// ==================== HISTORIQUE DES COURSES (sécurité) ====================
// Clé Maps navigateur (restreinte par référent au domaine admin) — override possible par env.
const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY || 'AIzaSyC2oz7Qx81F8BIP94WctOJL112Nm1rWJqQ';
const RIDE_TYPE_LABELS = { covoiturage: 'Covoiturage', mbiyo: 'Mbiyo', confort: 'Confort', premium: 'Premium', moto: 'Moto' };
const STATUS_META = {
  completed: { label: 'Terminée', color: '#166534', bg: '#ECFDF5' },
  cancelled_client: { label: 'Annulée (client)', color: '#991b1b', bg: '#fee2e2' },
  cancelled_driver: { label: 'Annulée (chauffeur)', color: '#991b1b', bg: '#fee2e2' },
  no_driver_found: { label: 'Aucun chauffeur', color: '#414852', bg: '#F4F5F7' },
  in_progress: { label: 'En cours', color: '#C9350F', bg: '#FEE9E2' },
  accepted: { label: 'Acceptée', color: '#C9350F', bg: '#FEE9E2' },
  driver_arriving: { label: 'En approche', color: '#C9350F', bg: '#FEE9E2' },
  driver_arrived: { label: 'Arrivé', color: '#C9350F', bg: '#FEE9E2' },
  searching: { label: 'Recherche', color: '#C9350F', bg: '#fef3c7' },
  scheduled: { label: 'Planifiée', color: '#C9350F', bg: '#fef3c7' },
  queued: { label: 'En file', color: '#C9350F', bg: '#fef3c7' },
};
const PAYMENT_METHOD_LABELS = { cash: 'Espèces', mobile_money: 'Mobile Money', mm: 'Mobile Money', balance: 'Solde', card: 'Carte' };
const PAYMENT_STATUS_LABELS = { paid: 'Payé', pending: 'En attente', failed: 'Échoué', refunded: 'Remboursé', unpaid: 'Impayé' };
const CANCELLED_SET = ['cancelled_client', 'cancelled_driver', 'no_driver_found'];

const fmtDateTime = (d) => { if (!d) return '—'; try { return new Date(d).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' }); } catch { return '—'; } };
const fmtMoney = (n, cur = 'CDF') => (n == null ? '—' : `${Number(n).toLocaleString('fr-FR')} ${cur}`);
const statusMeta = (s) => STATUS_META[s] || { label: s || '—', color: '#414852', bg: '#F4F5F7' };
const isRiskRide = (r) => CANCELLED_SET.includes(r.status) || (typeof r.rating_by_client === 'number' && r.rating_by_client <= 2);

const mapEndpoints = (ride) => {
  const p = ride.pickup || {}, d = ride.dropoff || {};
  const hasCoords = p.lat != null && p.lng != null && d.lat != null && d.lng != null;
  const origin = hasCoords ? `${p.lat},${p.lng}` : (p.address ? encodeURIComponent(p.address) : null);
  const dest = hasCoords ? `${d.lat},${d.lng}` : (d.address ? encodeURIComponent(d.address) : null);
  if (!origin || !dest) return null;
  const stops = (ride.stops || []).filter(s => s.lat != null && s.lng != null);
  const waypoints = stops.length ? stops.map(s => `${s.lat},${s.lng}`).join('|') : null;
  return { origin, dest, waypoints };
};
const mapEmbedUrl = (ride) => {
  const e = mapEndpoints(ride);
  if (!e) return null;
  const wp = e.waypoints ? `&waypoints=${e.waypoints}` : '';
  return `https://www.google.com/maps/embed/v1/directions?key=${MAPS_KEY}&origin=${e.origin}&destination=${e.dest}&mode=driving${wp}`;
};
const mapsExternalUrl = (ride) => {
  const e = mapEndpoints(ride);
  if (!e) return null;
  const wp = e.waypoints ? `&waypoints=${e.waypoints}` : '';
  return `https://www.google.com/maps/dir/?api=1&origin=${e.origin}&destination=${e.dest}&travelmode=driving${wp}`;
};

const copyText = (t) => { try { navigator.clipboard?.writeText(t); } catch { /* ignore */ } };

function StatusBadge({ status }) {
  const m = statusMeta(status);
  return <span style={{ background: m.bg, color: m.color, padding: '2px 10px', borderRadius: 999, fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap' }}>{m.label}</span>;
}

function DetailRow({ label, children }) {
  return (
    <div style={{ display: 'flex', gap: 8, fontSize: '0.85rem', padding: '3px 0' }}>
      <span style={{ color: '#5C636E', minWidth: 130, flexShrink: 0 }}>{label}</span>
      <span style={{ color: '#1E2229', fontWeight: 500 }}>{children}</span>
    </div>
  );
}

function RideDetail({ ride }) {
  const cur = ride.currency || 'CDF';
  const embed = mapEmbedUrl(ride);
  const ext = mapsExternalUrl(ride);
  const distance = ride.final_distance_km ?? ride.distance_traveled_km ?? ride.distance_km;
  const duration = ride.actual_duration_min ?? ride.estimated_duration_min;
  const sameName = !ride.passenger_name || ride.passenger_name === ride.client_name;
  return (
    <div style={{ padding: '12px 14px', background: '#FFFFFF', borderTop: '1px solid #E9EBEF', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
      {/* Colonne infos */}
      <div style={{ minWidth: 0 }}>
        <h5 style={sectionTitle}>👤 Client</h5>
        <DetailRow label="Nom">{ride.client_name || '—'}</DetailRow>
        <DetailRow label="Téléphone">
          {ride.client_phone ? (
            <span>
              <a href={`tel:${ride.client_phone}`} style={{ color: '#C9350F' }}>{ride.client_phone}</a>
              <button onClick={() => copyText(ride.client_phone)} title="Copier" style={miniBtn}>copier</button>
            </span>
          ) : '—'}
        </DetailRow>
        {!sameName && <DetailRow label="Passager">{ride.passenger_name} {ride.passenger_phone ? `· ${ride.passenger_phone}` : ''}</DetailRow>}

        <h5 style={sectionTitle}>📍 Trajet</h5>
        <DetailRow label="Départ">
          {ride.pickup?.address || '—'}
          {ride.pickup?.lat != null && <span style={coordChip} onClick={() => copyText(`${ride.pickup.lat},${ride.pickup.lng}`)} title="Copier les coordonnées">{ride.pickup.lat.toFixed(5)}, {ride.pickup.lng.toFixed(5)}</span>}
        </DetailRow>
        {(ride.stops || []).map((s, i) => (
          <DetailRow key={i} label={`Arrêt ${i + 1}`}>
            {s.address || '—'}
            {s.lat != null && <span style={coordChip} onClick={() => copyText(`${s.lat},${s.lng}`)} title="Copier">{s.lat.toFixed(5)}, {s.lng.toFixed(5)}</span>}
          </DetailRow>
        ))}
        <DetailRow label="Arrivée">
          {ride.dropoff?.address || '—'}
          {ride.dropoff?.lat != null && <span style={coordChip} onClick={() => copyText(`${ride.dropoff.lat},${ride.dropoff.lng}`)} title="Copier les coordonnées">{ride.dropoff.lat.toFixed(5)}, {ride.dropoff.lng.toFixed(5)}</span>}
        </DetailRow>
        <DetailRow label="Distance">{distance != null ? `${Number(distance).toFixed(1)} km` : '—'}</DetailRow>
        <DetailRow label="Durée">{duration != null ? `${Math.round(duration)} min` : '—'}</DetailRow>

        <h5 style={sectionTitle}>⏱️ Horodatage</h5>
        <DetailRow label="Demande">{fmtDateTime(ride.requested_at || ride.created_at)}</DetailRow>
        {ride.is_scheduled && <DetailRow label="Planifiée pour">{fmtDateTime(ride.scheduled_at)}</DetailRow>}
        <DetailRow label="Acceptée">{fmtDateTime(ride.accepted_at)}</DetailRow>
        <DetailRow label="Chauffeur arrivé">{fmtDateTime(ride.driver_arrived_at)}</DetailRow>
        <DetailRow label="Début course">{fmtDateTime(ride.started_at)}</DetailRow>
        <DetailRow label="Fin course">{fmtDateTime(ride.completed_at)}</DetailRow>
        {ride.cancelled_at && <DetailRow label="Annulée le">{fmtDateTime(ride.cancelled_at)}</DetailRow>}
      </div>

      {/* Colonne carte + prix + notes + annulation */}
      <div style={{ minWidth: 0 }}>
        <h5 style={sectionTitle}>🗺️ Carte du trajet</h5>
        {embed ? (
          <iframe title={`carte-${ride.ride_id}`} src={embed} style={{ width: '100%', height: 200, border: 0, borderRadius: 8 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" allowFullScreen />
        ) : (
          <div style={{ fontSize: '0.8rem', color: '#9AA0A8', padding: '10px 0' }}>Coordonnées GPS indisponibles pour cette course.</div>
        )}
        {ext && <a href={ext} target="_blank" rel="noreferrer" style={{ display: 'inline-block', marginTop: 6, fontSize: '0.8rem', color: '#C9350F', fontWeight: 600 }}>↗ Ouvrir dans Google Maps</a>}

        <h5 style={sectionTitle}>💰 Prix & paiement</h5>
        <DetailRow label="Prix estimé">{fmtMoney(ride.estimated_price, cur)}</DetailRow>
        <DetailRow label="Prix final">{fmtMoney(ride.final_price, cur)}</DetailRow>
        <DetailRow label="Mode">{PAYMENT_METHOD_LABELS[ride.payment_method] || ride.payment_method || '—'}</DetailRow>
        <DetailRow label="Statut paiement">{PAYMENT_STATUS_LABELS[ride.payment_status] || ride.payment_status || '—'}</DetailRow>

        <h5 style={sectionTitle}>⭐ Notes</h5>
        <DetailRow label="Client → chauffeur">{ride.rating_by_client != null ? `${'★'.repeat(ride.rating_by_client)}${'☆'.repeat(5 - ride.rating_by_client)} (${ride.rating_by_client}/5)` : '—'}</DetailRow>
        <DetailRow label="Chauffeur → client">{ride.rating_by_driver != null ? `${'★'.repeat(ride.rating_by_driver)}${'☆'.repeat(5 - ride.rating_by_driver)} (${ride.rating_by_driver}/5)` : '—'}</DetailRow>

        {CANCELLED_SET.includes(ride.status) && (
          <>
            <h5 style={{ ...sectionTitle, color: '#991b1b' }}>🚫 Annulation</h5>
            <DetailRow label="Motif">{ride.cancellation_reason || ride.cancellation_reason_code || '—'}</DetailRow>
            <DetailRow label="Phase">{ride.cancellation_phase || '—'}</DetailRow>
          </>
        )}
      </div>
    </div>
  );
}

const sectionTitle = { margin: '12px 0 4px', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: 0.4, color: '#2C313A', fontWeight: 700 };
const miniBtn = { marginLeft: 6, fontSize: '0.68rem', color: '#C9350F', background: '#FEE9E2', border: 'none', borderRadius: 4, padding: '1px 6px', cursor: 'pointer' };
const coordChip = { marginLeft: 8, fontSize: '0.7rem', color: '#5C636E', background: '#E9EBEF', borderRadius: 4, padding: '1px 6px', cursor: 'pointer' };

function DriverHistoryModal({ driver, onClose }) {
  const driverId = driver.driver_id || driver.id;
  const [status, setStatus] = React.useState('all');
  const [from, setFrom] = React.useState('');
  const [to, setTo] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [expandedId, setExpandedId] = React.useState(null);

  React.useEffect(() => { setPage(1); }, [status, from, to]);

  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true); setError(null);
      try {
        const params = new URLSearchParams({ status, page: String(page), limit: '25' });
        if (from) params.set('from', from);
        if (to) params.set('to', to);
        const res = await apiService.get(`/admin/drivers/${driverId}/rides?${params.toString()}`, { userType: 'admin' });
        if (cancelled) return;
        if (res && res.success) setData(res);
        else setError(res?.error || 'Erreur de chargement');
      } catch (e) {
        if (!cancelled) setError(e?.error || e?.message || 'Erreur de chargement');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [driverId, status, from, to, page]);

  const summary = data?.summary;
  const rides = data?.rides || [];

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modalCard} onClick={(e) => e.stopPropagation()}>
        {/* En-tête */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #E9EBEF' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#1E2229' }}>📜 Historique des courses</h3>
            <p style={{ margin: '2px 0 0', fontSize: '0.85rem', color: '#5C636E' }}>{driver.name} · {driver.phone}</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.6rem', cursor: 'pointer', color: '#9AA0A8', lineHeight: 1 }}>×</button>
        </div>

        {/* Résumé sécurité */}
        {summary && (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', padding: '12px 20px', background: '#FFFFFF', borderBottom: '1px solid #E9EBEF' }}>
            <SummaryStat label="Courses" value={summary.total} />
            <SummaryStat label="Terminées" value={summary.completed} color="#166534" />
            <SummaryStat label="Annulées" value={summary.cancelled} color="#991b1b" />
            <SummaryStat label="Note moy." value={summary.avg_rating != null ? `${summary.avg_rating} ★ (${summary.rating_count})` : '—'} />
            <SummaryStat label="Notes ≤ 2" value={summary.low_rating_count} color={summary.low_rating_count > 0 ? '#B91C1C' : undefined} />
          </div>
        )}

        {/* Filtres */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', padding: '12px 20px', borderBottom: '1px solid #E9EBEF' }}>
          <select value={status} onChange={(e) => setStatus(e.target.value)} style={filterInput}>
            <option value="all">Tous les statuts</option>
            <option value="completed">Terminées</option>
            <option value="cancelled">Annulées</option>
          </select>
          <label style={{ fontSize: '0.8rem', color: '#5C636E' }}>Du <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={filterInput} /></label>
          <label style={{ fontSize: '0.8rem', color: '#5C636E' }}>Au <input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={filterInput} /></label>
          {(from || to || status !== 'all') && <button onClick={() => { setStatus('all'); setFrom(''); setTo(''); }} style={{ ...miniBtn, background: '#F4F5F7', color: '#414852' }}>réinitialiser</button>}
        </div>

        {/* Liste */}
        <div style={{ overflowY: 'auto', padding: '12px 20px', flex: 1 }}>
          {loading && <p style={{ color: '#5C636E', textAlign: 'center', padding: 20 }}>Chargement…</p>}
          {error && <p style={{ color: '#B91C1C', textAlign: 'center', padding: 20 }}>{error}</p>}
          {!loading && !error && rides.length === 0 && <p style={{ color: '#9AA0A8', textAlign: 'center', padding: 20 }}>Aucune course pour ces critères.</p>}
          {!loading && !error && rides.map((r) => {
            const risk = isRiskRide(r);
            const open = expandedId === r.ride_id;
            return (
              <div key={r.ride_id} style={{ border: `1px solid ${risk ? '#fca5a5' : '#E9EBEF'}`, borderRadius: 10, marginBottom: 10, overflow: 'hidden', background: risk ? '#fef2f2' : 'white' }}>
                <div onClick={() => setExpandedId(open ? null : r.ride_id)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', cursor: 'pointer' }}>
                  <span style={{ fontSize: '0.82rem', color: '#5C636E', minWidth: 150 }}>{fmtDateTime(r.requested_at || r.created_at)}</span>
                  <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#2C313A', minWidth: 80 }}>{RIDE_TYPE_LABELS[r.ride_type] || r.ride_type}</span>
                  <span style={{ flex: 1, minWidth: 0, fontSize: '0.82rem', color: '#414852', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {(r.pickup?.address || '?')} → {(r.dropoff?.address || '?')}
                  </span>
                  {risk && <span title="Course à surveiller" style={{ fontSize: '0.9rem' }}>⚠️</span>}
                  <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#1E2229', minWidth: 90, textAlign: 'right' }}>{fmtMoney(r.final_price ?? r.estimated_price, r.currency || 'CDF')}</span>
                  <StatusBadge status={r.status} />
                  <span style={{ color: '#9AA0A8', fontSize: '0.9rem' }}>{open ? '▲' : '▼'}</span>
                </div>
                {open && <RideDetail ride={r} />}
              </div>
            );
          })}
        </div>

        {/* Pagination */}
        {data && data.pages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, padding: '12px 20px', borderTop: '1px solid #E9EBEF' }}>
            <button disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} style={pagerBtn(page <= 1)}>← Précédent</button>
            <span style={{ fontSize: '0.82rem', color: '#5C636E' }}>Page {data.page} / {data.pages} · {data.total} courses</span>
            <button disabled={page >= data.pages} onClick={() => setPage(p => p + 1)} style={pagerBtn(page >= data.pages)}>Suivant →</button>
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryStat({ label, value, color }) {
  return (
    <div style={{ background: 'white', border: '1px solid #E9EBEF', borderRadius: 8, padding: '6px 12px', minWidth: 90 }}>
      <div style={{ fontSize: '0.68rem', color: '#5C636E', textTransform: 'uppercase', letterSpacing: 0.3 }}>{label}</div>
      <div style={{ fontSize: '1.05rem', fontWeight: 700, color: color || '#1E2229' }}>{value}</div>
    </div>
  );
}

const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 };
const modalCard = { background: 'white', borderRadius: 14, width: 'min(900px, 96vw)', maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' };
const filterInput = { padding: '6px 10px', border: '2px solid #E9EBEF', borderRadius: 8, fontSize: '0.85rem', color: '#1E2229', marginLeft: 4 };
const pagerBtn = (disabled) => ({ padding: '6px 14px', borderRadius: 8, border: '1px solid #E9EBEF', background: disabled ? '#F4F5F7' : 'white', color: disabled ? '#DCE0E6' : '#C9350F', cursor: disabled ? 'default' : 'pointer', fontWeight: 600, fontSize: '0.82rem' });
